import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Save, RotateCcw, Loader2, ArrowLeft, Eye, EyeOff, Trash2 } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';

const API = process.env.REACT_APP_BACKEND_URL || '';

const FieldMappingTab = () => {
  const [mapping, setMapping] = useState({});
  const [visibility, setVisibility] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // System-Felder die gemappt werden können
  const systemFields = [
    { key: 'vorname', label: 'Vorname', required: true },
    { key: 'nachname', label: 'Nachname', required: true },
    { key: 'geburtsdatum', label: 'Geburtsdatum', required: false },
    { key: 'email', label: 'E-Mail', required: false },
    { key: 'telefon', label: 'Telefon', required: false },
    { key: 'theoriekurs', label: 'Kurs', required: true, description: 'Verknüpft Teilnehmer mit Kursdetails' },
    { key: 'pruefung_stufe', label: 'Prüfung: Stufe', required: false },
    { key: 'pruefung_punkte', label: 'Prüfung: Punkte', required: false },
    { key: 'pruefung_note', label: 'Prüfung: Note', required: false },
  ];

  // Standard CSV-Spalten für MSY
  const defaultCsvMapping = {
    'vorname': 'Vorname',
    'nachname': 'Nachname',
    'geburtsdatum': 'Geburtsdatum',
    'email': 'Mail Erziehungsberechtigter',
    'telefon': 'Telefonnummer Erziehungsberechtigter',
    'theoriekurs': 'Kurs',
    'pruefung_stufe': 'Stufe',
    'pruefung_punkte': 'Punkte',
    'pruefung_note': 'Note',
  };

  useEffect(() => {
    fetchMapping();
  }, []);

  const fetchMapping = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/api/admin/field-mapping`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Convert old format (csv -> system) to new format (system -> csv)
      const oldMappings = response.data.mappings || {};
      const newMapping = {};
      
      // Reverse the mapping: from {csv: system} to {system: csv}
      Object.entries(oldMappings).forEach(([csvCol, systemField]) => {
        // Only include valid system fields
        if (systemFields.some(f => f.key === systemField)) {
          newMapping[systemField] = csvCol;
        }
      });
      
      setMapping(newMapping);
      setVisibility(response.data.visibility || {});
    } catch (error) {
      toast.error('Fehler beim Laden des Feldmappings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      // Convert back to old format for API: {csv: system}
      const apiMapping = {};
      Object.entries(mapping).forEach(([systemField, csvCol]) => {
        if (csvCol && csvCol.trim()) {
          apiMapping[csvCol] = systemField;
        }
      });
      
      await axios.put(`${API}/api/admin/field-mapping`, {
        mappings: apiMapping,
        visibility: visibility
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Feldmapping gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleCsvColumnChange = (systemField, csvColumn) => {
    setMapping(prev => ({
      ...prev,
      [systemField]: csvColumn
    }));
  };

  const handleToggleVisibility = (systemField) => {
    setVisibility(prev => ({
      ...prev,
      [systemField]: prev[systemField] === false ? true : false
    }));
  };

  const handleClearField = (systemField) => {
    setMapping(prev => {
      const newMapping = { ...prev };
      delete newMapping[systemField];
      return newMapping;
    });
  };

  const loadDefaultMapping = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/api/admin/field-mapping/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Convert from API format {csv: system} to UI format {system: csv}
      const apiMappings = response.data.mappings || {};
      const newMapping = {};
      Object.entries(apiMappings).forEach(([csvCol, systemField]) => {
        newMapping[systemField] = csvCol;
      });
      
      setMapping(newMapping);
      setVisibility(response.data.visibility || {});
      toast.success('Standard-Mapping geladen und in Datenbank gespeichert');
    } catch (error) {
      toast.error('Fehler beim Laden des Standard-Mappings');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Ordne Systemfelder den CSV-Spalten zu. Kursdaten (Ort, Zeit, Lehrer) werden aus der Kursverwaltung geladen.
        </p>
        <div className="flex gap-2">
          <Button
            onClick={loadDefaultMapping}
            variant="outline"
            size="sm"
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            Standard laden
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            size="sm"
            className="bg-blue-600 hover:bg-blue-700"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            Speichern
          </Button>
        </div>
      </div>

      {/* Mapping Table */}
      <Card>
        <CardHeader className="py-3">
          <div className="grid grid-cols-12 gap-4 text-xs font-semibold text-gray-500 uppercase">
            <div className="col-span-1">Sichtbar</div>
            <div className="col-span-4">Systemfeld</div>
            <div className="col-span-1 text-center">→</div>
            <div className="col-span-5">CSV-Spaltenname</div>
            <div className="col-span-1"></div>
          </div>
        </CardHeader>
        <CardContent className="pt-0 space-y-2">
          {systemFields.map((field) => {
            const isVisible = visibility[field.key] !== false;
            const csvValue = mapping[field.key] || '';
            
            return (
              <div 
                key={field.key}
                className={`grid grid-cols-12 gap-4 items-center p-3 rounded-lg border ${
                  isVisible ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100 opacity-60'
                }`}
              >
                {/* Visibility Toggle */}
                <div className="col-span-1">
                  <Checkbox
                    checked={isVisible}
                    onCheckedChange={() => handleToggleVisibility(field.key)}
                  />
                </div>
                
                {/* System Field */}
                <div className="col-span-4">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{field.label}</span>
                    {field.required && <span className="text-red-500 text-xs">*</span>}
                  </div>
                  {field.description && (
                    <p className="text-xs text-gray-500 mt-0.5">{field.description}</p>
                  )}
                </div>
                
                {/* Arrow */}
                <div className="col-span-1 text-center">
                  <ArrowLeft className="h-4 w-4 text-gray-400 rotate-180 mx-auto" />
                </div>
                
                {/* CSV Column Input */}
                <div className="col-span-5">
                  <Input
                    value={csvValue}
                    onChange={(e) => handleCsvColumnChange(field.key, e.target.value)}
                    placeholder="CSV-Spaltenname eingeben"
                    className="h-9 text-sm"
                  />
                </div>
                
                {/* Clear Button */}
                <div className="col-span-1 flex justify-end">
                  {csvValue && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleClearField(field.key)}
                      className="h-8 w-8 p-0 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-3">
          <p className="text-sm text-blue-800">
            <strong>Hinweis:</strong> Die CSV-Spaltennamen müssen exakt mit den Spalten in Ihrer CSV-Datei übereinstimmen (Groß-/Kleinschreibung beachten).
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default FieldMappingTab;
